/**
 * Self-Cert Entity Models
 * Type-safe models for Self-Cert Entity form data handling, validation, and XML export
 */

import { generateSelfCertEntityXML } from "../components/SendingForms/utils/generateSelfCertEntityXML";

// API Response Model - Complete form fields from API
export class SelfCertEntityResponseModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Entity";
  "UniqueIdentifier": string = "";

  // Section 1 - Entity Information
  "SC-Entity-Section_1_entity_name": string = "";
  "SC-Entity-Section_1_org_country": string = "";

  // Section 1 - Current Residence Address
  "SC-Entity-Section_1_current_residence_address_street": string = "";
  "SC-Entity-Section_1_current_residence_address_city": string = "";
  "SC-Entity-Section_1_current_residence_address_state": string = "";
  "SC-Entity-Section_1_current_residence_address_country": string = "";
  "SC-Entity-Section_1_current_residence_address_postal": string = "";

  // Section 1 - Mailing Address
  "SC-Entity-Section_1_mailing_address_street": string = "";
  "SC-Entity-Section_1_mailing_address_city": string = "";
  "SC-Entity-Section_1_mailing_address_state": string = "";
  "SC-Entity-Section_1_mailing_address_country": string = "";
  "SC-Entity-Section_1_mailing_address_postal": string = "";

  // Section 2 - Tax Residence Information
  "SC-Entity-Section_2_tax_residence": string = "";

  // Signature Information
  "SC-Entity-Signature": string = "";
  "SC-Entity-Print_name": string = "";
  "SC-Entity-Capacity": string = "";
  "SC-Entity-Date": string = "";
  "SC-Entity-Section_4_Signature_unreadable": boolean = false;
  "SC-Entity-isSigned": string = "no";
  "SC-Entity-isModified": string = "no";

  // PART IV - Controlling Persons
  "SC-Entity-Search_Entity": string = "";
  "SC-Entity-Controlling_person_title": string = "";
  "SC-Entity-Controlling_person_last_name": string = "";
  "SC-Entity-Controlling_person_first_name": string = "";
  "SC-Entity-Controlling_person_middle_name": string = "";
  "SC-Entity-Legal_entity": string = "";

  // Current Residence Address
  "SC-Entity-Controlling_person_address_street": string = "";
  "SC-Entity-Controlling_person_address_street2": string = "";
  "SC-Entity-Controlling_person_address_city": string = "";
  "SC-Entity-Controlling_person_address_country": string = "";
  "SC-Entity-Controlling_person_address_postal": string = "";

  // Mailing Address
  "SC-Entity-Controlling_person_mailing_address_street": string = "";
  "SC-Entity-Controlling_person_mailing_address_city": string = "";
  "SC-Entity-Controlling_person_mailing_address_state": string = "";
  "SC-Entity-Controlling_person_mailing_address_country": string = "";
  "SC-Entity-Controlling_person_mailing_address_postal": string = "";

  // Date of Birth
  "SC-Entity-Controlling_person_dob": string = "";
  "SC-Entity-Controlling_person_dob_display": string = "";

  // Place of Birth
  "SC-Entity-Controlling_person_city_of_birth": string = "";
  "SC-Entity-Controlling_person_country_of_birth": string = "";

  // Type of Controlling Person
  "SC-Entity-Controlling_person_type_owner": boolean = false;
  "SC-Entity-Controlling_person_owner_percentage": string = "";
  "SC-Entity-Controlling_person_by_other_means": boolean = false;
  "SC-Entity-Controlling_person_senior_managing": boolean = false;

  // Trust options
  "SC-Entity-Controlling_person_settlor": boolean = false;
  "SC-Entity-Controlling_person_trustee": boolean = false;
  "SC-Entity-Controlling_person_protector": boolean = false;
  "SC-Entity-Controlling_person_beneficiary": boolean = false;
  "SC-Entity-Controlling_person_other": boolean = false;
  "SC-Entity-Controlling_person_other_specify": string = "";

  // Legal arrangement options
  "SC-Entity-Controlling_person_settlor_equivalent": boolean = false;
  "SC-Entity-Controlling_person_trustee_equivalent": boolean = false;
  "SC-Entity-Controlling_person_protector_equivalent": boolean = false;
  "SC-Entity-Controlling_person_beneficiary_equivalent": boolean = false;
  "SC-Entity-Controlling_person_other_equivalent": boolean = false;
  "SC-Entity-Controlling_person_other_equivalent_specify": string = "";

  // Additional Jurisdictions (2-4)
  "SC-Entity-Controlling_person_jurisdiction_2": string = "";
  "SC-Entity-Controlling_person_tax_id_2": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_2": string = "";
  "SC-Entity-Controlling_person_tin_reason_2": string = "";

  "SC-Entity-Controlling_person_jurisdiction_3": string = "";
  "SC-Entity-Controlling_person_tax_id_3": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_3": string = "";
  "SC-Entity-Controlling_person_tin_reason_3": string = "";

  "SC-Entity-Controlling_person_jurisdiction_4": string = "";
  "SC-Entity-Controlling_person_tax_id_4": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_4": string = "";
  "SC-Entity-Controlling_person_tin_reason_4": string = "";

  // Additional Information Section
  "SC-Entity-Controlling_person_additional_info": string = "";
  "SC-Entity-Controlling_person_relationship": string = "";
  "SC-Entity-Controlling_person_control_percentage": string = "";
  "SC-Entity-Controlling_person_control_date": string = "";
  "SC-Entity-Controlling_person_control_method": string = "";

  // Signature Section
  "SC-Entity-Section_5_signature": string = "";
  "SC-Entity-Section_5_signer_printed_name": string = "";
  "SC-Entity-Section_5_signature_date": string = "";
  "SC-Entity-Section_5_capacity": string = "";

  constructor(data?: any) {
    if (data) {
      Object.assign(this, data);
    }
  }
}

export class SelfCertEntityFormModel {
  // Dynamic fields support
  [key: string]: any;

  // Core Form Fields
  "Form_name": string = "Self-Cert Entity";
  "UniqueIdentifier": string = "";

  // Section 1 - Entity Information
  "SC-Entity-Section_1_entity_name": string = "";
  "SC-Entity-Section_1_org_country": string = "";

  // Section 1 - Current Residence Address
  "SC-Entity-Section_1_current_residence_address_street": string = "";
  "SC-Entity-Section_1_current_residence_address_city": string = "";
  "SC-Entity-Section_1_current_residence_address_state": string = "";
  "SC-Entity-Section_1_current_residence_address_country": string = "";
  "SC-Entity-Section_1_current_residence_address_postal": string = "";

  // Section 1 - Mailing Address
  "SC-Entity-Section_1_mailing_address_street": string = "";
  "SC-Entity-Section_1_mailing_address_city": string = "";
  "SC-Entity-Section_1_mailing_address_state": string = "";
  "SC-Entity-Section_1_mailing_address_country": string = "";
  "SC-Entity-Section_1_mailing_address_postal": string = "";

  // Section 2 - Tax Residence Information
  "SC-Entity-Section_2_tax_residence": string = "";

  // Signature Information
  "SC-Entity-Signature": string = "";
  "SC-Entity-Print_name": string = "";
  "SC-Entity-Capacity": string = "";
  "SC-Entity-Date": string = "";
  "SC-Entity-Section_4_Signature_unreadable": boolean = false;
  "SC-Entity-isSigned": string = "no";
  "SC-Entity-isModified": string = "no";

  // PART IV - Controlling Persons
  "SC-Entity-Search_Entity": string = "";
  "SC-Entity-Controlling_person_title": string = "";
  "SC-Entity-Controlling_person_last_name": string = "";
  "SC-Entity-Controlling_person_first_name": string = "";
  "SC-Entity-Controlling_person_middle_name": string = "";
  "SC-Entity-Legal_entity": string = "";

  // Current Residence Address
  "SC-Entity-Controlling_person_address_street": string = "";
  "SC-Entity-Controlling_person_address_street2": string = "";
  "SC-Entity-Controlling_person_address_city": string = "";
  "SC-Entity-Controlling_person_address_country": string = "";
  "SC-Entity-Controlling_person_address_postal": string = "";

  // Mailing Address
  "SC-Entity-Controlling_person_mailing_address_street": string = "";
  "SC-Entity-Controlling_person_mailing_address_city": string = "";
  "SC-Entity-Controlling_person_mailing_address_state": string = "";
  "SC-Entity-Controlling_person_mailing_address_country": string = "";
  "SC-Entity-Controlling_person_mailing_address_postal": string = "";

  // Date of Birth
  "SC-Entity-Controlling_person_dob": string = "";
  "SC-Entity-Controlling_person_dob_display": string = "";

  // Place of Birth
  "SC-Entity-Controlling_person_city_of_birth": string = "";
  "SC-Entity-Controlling_person_country_of_birth": string = "";

  // Type of Controlling Person
  "SC-Entity-Controlling_person_type_owner": boolean = false;
  "SC-Entity-Controlling_person_owner_percentage": string = "";
  "SC-Entity-Controlling_person_by_other_means": boolean = false;
  "SC-Entity-Controlling_person_senior_managing": boolean = false;

  // Trust options
  "SC-Entity-Controlling_person_settlor": boolean = false;
  "SC-Entity-Controlling_person_trustee": boolean = false;
  "SC-Entity-Controlling_person_protector": boolean = false;
  "SC-Entity-Controlling_person_beneficiary": boolean = false;
  "SC-Entity-Controlling_person_other": boolean = false;
  "SC-Entity-Controlling_person_other_specify": string = "";

  // Legal arrangement options
  "SC-Entity-Controlling_person_settlor_equivalent": boolean = false;
  "SC-Entity-Controlling_person_trustee_equivalent": boolean = false;
  "SC-Entity-Controlling_person_protector_equivalent": boolean = false;
  "SC-Entity-Controlling_person_beneficiary_equivalent": boolean = false;
  "SC-Entity-Controlling_person_other_equivalent": boolean = false;
  "SC-Entity-Controlling_person_other_equivalent_specify": string = "";

  // Additional Jurisdictions (2-4)
  "SC-Entity-Controlling_person_jurisdiction_2": string = "";
  "SC-Entity-Controlling_person_tax_id_2": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_2": string = "";
  "SC-Entity-Controlling_person_tin_reason_2": string = "";
  "SC-Entity-Section_4_sole_residency_yes": boolean = false;
  "SC-Entity-Section_4_sole_residency_no": boolean = false;
  "SC-Entity-Section_4_tax_id": string = "";
  "SC-Entity-Section_4_jurisdiction_1": string = "";
  "SC-Entity-Section_4_unable_to_procure_tin": boolean = false;
  "SC-Entity-Section_4_tax_reference_number": string = "";
  "SC-Entity-Section_4_reason_non_availability_of_tin": string = "";

  "SC-Entity-Controlling_person_jurisdiction_3": string = "";
  "SC-Entity-Controlling_person_tax_id_3": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_3": string = "";
  "SC-Entity-Controlling_person_tin_reason_3": string = "";

  "SC-Entity-Controlling_person_jurisdiction_4": string = "";
  "SC-Entity-Controlling_person_tax_id_4": string = "";
  "SC-Entity-Controlling_person_tax_reference_type_4": string = "";
  "SC-Entity-Controlling_person_tin_reason_4": string = "";

  // Additional Information Section
  "SC-Entity-Controlling_person_additional_info": string = "";
  "SC-Entity-Controlling_person_relationship": string = "";
  "SC-Entity-Controlling_person_control_percentage": string = "";
  "SC-Entity-Controlling_person_control_date": string = "";
  "SC-Entity-Controlling_person_control_method": string = "";

  // Signature Section
  "SC-Entity-Section_5_signature": string = "";
  "SC-Entity-Section_5_signer_printed_name": string = "";
  "SC-Entity-Section_5_signature_date": string = "";
  "SC-Entity-Section_5_capacity": string = "";

  // Mapping control flag
  "isMapDone": boolean = false;

  // Section 11: Controlling Persons Table (1-10)
  "SC-Entity-Controlling_person_1": string = "";
  "SC-Entity-Controlling_person_2": string = "";
  "SC-Entity-Controlling_person_3": string = "";
  "SC-Entity-Controlling_person_4": string = "";
  "SC-Entity-Controlling_person_5": string = "";
  "SC-Entity-Controlling_person_6": string = "";
  "SC-Entity-Controlling_person_7": string = "";
  "SC-Entity-Controlling_person_8": string = "";
  "SC-Entity-Controlling_person_9": string = "";
  "SC-Entity-Controlling_person_10": string = "";

  constructor(data?: Partial<SelfCertEntityFormModel>) {
    if (data) {
      Object.assign(this, data);
    }
  }

  /**
   * Generate XML representation using the Self-Cert Entity XML format
   */
  async toXML(): Promise<string> {
    // Map model data to the format expected by XML generator
    const xmlData = {
      // Map form fields to XML field names
      BusinessUnit: "", // Will need to be provided or mapped
      Organization_1: this["SC-Entity-Section_1_entity_name"],
      CountryOfOrganization: this["SC-Entity-Section_1_org_country"],

      // Permanent Address (using current residence address)
      PermenantAddress_Line1: this["SC-Entity-Section_1_current_residence_address_street"],
      PermenantAddress_City: this["SC-Entity-Section_1_current_residence_address_city"],
      PermenantAddress_State: this["SC-Entity-Section_1_current_residence_address_state"],
      PermenantAddress_Country: this["SC-Entity-Section_1_current_residence_address_country"],
      PermenantAddress_Zip: this["SC-Entity-Section_1_current_residence_address_postal"],

      // Mailing Address
      MailingAddress_Line1: this["SC-Entity-Section_1_mailing_address_street"],
      MailingAddress_City: this["SC-Entity-Section_1_mailing_address_city"],
      MailingAddress_State: this["SC-Entity-Section_1_mailing_address_state"],
      MailingAddress_Country: this["SC-Entity-Section_1_mailing_address_country"],
      MailingAddress_Zip: this["SC-Entity-Section_1_mailing_address_postal"],

      // Tax Information
      TaxResidencyCountry: this["SC-Entity-Section_2_tax_residence"],

      // Signature Information

      SignedBy: this["SC-Entity-Print_name"],
      Capacity: this["SC-Entity-Capacity"],
      SignDate: this["SC-Entity-Date"],
      Form_OCR_Signature: this["SC-Entity-Signature"],

      // Include all other fields from the model
      ...this,
    };

    return await generateSelfCertEntityXML(xmlData);
  }

  /**
   * Convert to API payload format
   */
  toApiPayload(): Record<string, any> {
    return {
      ...this,
    };
  }

  /**
   * Validate the form data
   */
  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Required field validations
    if (!this["SC-Entity-Section_1_entity_name"]) {
      errors.push("Entity name is required");
    }
    if (!this["SC-Entity-Section_1_org_country"]) {
      errors.push("Country of organization is required");
    }
    if (!this["SC-Entity-Section_2_tax_residence"]) {
      errors.push("Country of tax residence is required");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Self-Cert Entity Field Mapper - Maps response model fields to form model fields
 */
export class SelfCertEntityFieldMapper {
  /**
   * Map response model to form model
   */
  static mapResponseToForm(responseModel: SelfCertEntityResponseModel): SelfCertEntityFormModel {
    const formModel = new SelfCertEntityFormModel();

    // Check if mapping is already done
    if (responseModel["isMapDone"]) {
      // If already mapped, just return the existing data as form model
      Object.assign(formModel, responseModel);
      return formModel;
    }

    // Direct field mappings
    const directMappings = [
      "UniqueIdentifier",
      "entityUniqueIdentifier",
      "Form_name",
      "SC-Entity-Section_1_entity_name",
      "SC-Entity-Section_1_org_country",
      "SC-Entity-Section_1_current_residence_address_street",
      "SC-Entity-Section_1_current_residence_address_city",
      "SC-Entity-Section_1_current_residence_address_state",
      "SC-Entity-Section_1_current_residence_address_country",
      "SC-Entity-Section_1_current_residence_address_postal",
      "SC-Entity-Section_1_mailing_address_street",
      "SC-Entity-Section_1_mailing_address_city",
      "SC-Entity-Section_1_mailing_address_state",
      "SC-Entity-Section_1_mailing_address_country",
      "SC-Entity-Section_1_mailing_address_postal",
      // Hyphenated variants
      "SC-Entity-Section_1_entity_name",
      "SC-Entity-Section_1_org_country",
      "SC-Entity-Section_1_current_residence_address_street",
      "SC-Entity-Section_1_current_residence_address_city",
      "SC-Entity-Section_1_current_residence_address_state",
      "SC-Entity-Section_1_current_residence_address_country",
      "SC-Entity-Section_1_current_residence_address_postal",
      "SC-Entity-Section_1_mailing_address_street",
      "SC-Entity-Section_1_mailing_address_city",
      "SC-Entity-Section_1_mailing_address_state",
      "SC-Entity-Section_1_mailing_address_country",
      "SC-Entity-Section_1_mailing_address_postal",
      "SC-Entity-Section_2_tax_residence",
      "SC-Entity-Section_2_unable_to_procure_a_tin",
      "SC-Entity-Section_2_unable_to_procure_a_tin",
      "SC-Entity-Signature",
      "SC-Entity-Print_name",
      "SC-Entity-Capacity",
      "SC-Entity-Date",
      "SC-Entity-Section_4_Signature_unreadable",
      "SC-Entity-isSigned",
      "SC-Entity-isModified",

      // PART IV - Controlling Persons
      "SC-Entity-Search_Entity",
      "SC-Entity-Controlling_person_title",
      "SC-Entity-Controlling_person_last_name",
      "SC-Entity-Controlling_person_first_name",
      "SC-Entity-Controlling_person_middle_name",
      "SC-Entity-Legal_entity",
      "SC-Entity-Controlling_person_address_street",
      "SC-Entity-Controlling_person_address_street2",
      "SC-Entity-Controlling_person_address_city",
      "SC-Entity-Controlling_person_address_country",
      "SC-Entity-Controlling_person_address_postal",
      "SC-Entity-Controlling_person_mailing_address_street",
      "SC-Entity-Controlling_person_mailing_address_city",
      "SC-Entity-Controlling_person_mailing_address_state",
      "SC-Entity-Controlling_person_mailing_address_country",
      "SC-Entity-Controlling_person_mailing_address_postal",
      "SC-Entity-Controlling_person_dob",
      "SC-Entity-Controlling_person_dob_display",
      "SC-Entity-Controlling_person_city_of_birth",
      "SC-Entity-Controlling_person_country_of_birth",
      "SC-Entity-Controlling_person_type_owner",
      "SC-Entity-Controlling_person_owner_percentage",
      "SC-Entity-Controlling_person_by_other_means",
      "SC-Entity-Controlling_person_senior_managing",
      "SC-Entity-Controlling_person_settlor",
      "SC-Entity-Controlling_person_trustee",
      "SC-Entity-Controlling_person_protector",
      "SC-Entity-Controlling_person_beneficiary",
      "SC-Entity-Controlling_person_other",
      "SC-Entity-Controlling_person_other_specify",
      "SC-Entity-Controlling_person_settlor_equivalent",
      "SC-Entity-Controlling_person_trustee_equivalent",
      "SC-Entity-Controlling_person_protector_equivalent",
      "SC-Entity-Controlling_person_beneficiary_equivalent",
      "SC-Entity-Controlling_person_other_equivalent",
      "SC-Entity-Controlling_person_other_equivalent_specify",
      "SC-Entity-Controlling_person_other_specify",
      "SC-Entity-Controlling_person_other_equivalent_specify",

      "SC-Entity-Controlling_person_jurisdiction_2",
      "SC-Entity-Controlling_person_tax_id_2",
      "SC-Entity-Controlling_person_tax_reference_type_2",
      "SC-Entity-Controlling_person_tin_reason_2",
      "SC-Entity-Controlling_person_jurisdiction_3",
      "SC-Entity-Controlling_person_tax_id_3",
      "SC-Entity-Controlling_person_tax_reference_type_3",
      "SC-Entity-Controlling_person_tin_reason_3",
      "SC-Entity-Controlling_person_jurisdiction_4",
      "SC-Entity-Controlling_person_tax_id_4",
      "SC-Entity-Controlling_person_tax_reference_type_4",
      "SC-Entity-Controlling_person_tin_reason_4",
      "SC-Entity-Controlling_person_additional_info",
      "SC-Entity-Controlling_person_relationship",
      "SC-Entity-Controlling_person_control_percentage",
      "SC-Entity-Controlling_person_control_date",
      "SC-Entity-Controlling_person_control_method",
      "SC-Entity-Section_5_signature",
      "SC-Entity-Section_5_signer_printed_name",
      "SC-Entity-Section_5_signature_date",
      "SC-Entity-Section_5_capacity",
      // Section 11: Controlling Persons Table
      "SC-Entity-Controlling_person_1",
      "SC-Entity-Controlling_person_2",
      "SC-Entity-Controlling_person_3",
      "SC-Entity-Controlling_person_4",
      "SC-Entity-Controlling_person_5",
      "SC-Entity-Controlling_person_6",
      "SC-Entity-Controlling_person_7",
      "SC-Entity-Controlling_person_8",
      "SC-Entity-Controlling_person_9",
      "SC-Entity-Controlling_person_10",
    ];

    directMappings.forEach((field) => {
      if (responseModel[field] !== undefined) {
        formModel[field] = responseModel[field];
      }
    });

    // Special mappings
    this.mapSpecialFields(responseModel, formModel);

    // Auto-set isSigned and Signature_unreadable based on Signature field (only during initial mapping)
    if (formModel["SC-Entity-Signature"] && formModel["SC-Entity-Signature"].trim() !== "") {
      formModel["SC-Entity-isSigned"] = "yes";
      formModel["SC-Entity-Section_4_Signature_unreadable"] = true;
    }

    // Set isMapDone to true to prevent re-mapping
    formModel.isMapDone = true;

    return formModel;
  }

  /**
   * Handle special field mappings and transformations
   */
  private static mapSpecialFields(
    responseModel: SelfCertEntityResponseModel,
    formModel: SelfCertEntityFormModel
  ): void {
    // Map Section 1 fields from hyphenated API names to underscored form names
    if (responseModel["SC-Entity-Section_1_entity_name"]) {
      formModel["SC-Entity-Section_1_entity_name"] =
        responseModel["SC-Entity-Section_1_entity_name"];
    }
    if (responseModel["SC-Entity-Section_1_org_country"]) {
      formModel["SC-Entity-Section_1_org_country"] =
        responseModel["SC-Entity-Section_1_org_country"];
    }
    if (responseModel["SC-Entity-Section_1_current_residence_address_street"]) {
      formModel["SC-Entity-Section_1_current_residence_address_street"] =
        responseModel["SC-Entity-Section_1_current_residence_address_street"];
    }
    if (responseModel["SC-Entity-Section_1_current_residence_address_city"]) {
      formModel["SC-Entity-Section_1_current_residence_address_city"] =
        responseModel["SC-Entity-Section_1_current_residence_address_city"];
    }
    if (responseModel["SC-Entity-Section_1_current_residence_address_state"]) {
      formModel["SC-Entity-Section_1_current_residence_address_state"] =
        responseModel["SC-Entity-Section_1_current_residence_address_state"];
    }
    if (responseModel["SC-Entity-Section_1_current_residence_address_country"]) {
      formModel["SC-Entity-Section_1_current_residence_address_country"] =
        responseModel["SC-Entity-Section_1_current_residence_address_country"];
    }
    if (responseModel["SC-Entity-Section_1_current_residence_address_postal"]) {
      formModel["SC-Entity-Section_1_current_residence_address_postal"] =
        responseModel["SC-Entity-Section_1_current_residence_address_postal"];
    }

    // Map Section 1 mailing address
    if (responseModel["SC-Entity-Section_1_mailing_address_street"]) {
      formModel["SC-Entity-Section_1_mailing_address_street"] =
        responseModel["SC-Entity-Section_1_mailing_address_street"];
    }
    if (responseModel["SC-Entity-Section_1_mailing_address_city"]) {
      formModel["SC-Entity-Section_1_mailing_address_city"] =
        responseModel["SC-Entity-Section_1_mailing_address_city"];
    }
    if (responseModel["SC-Entity-Section_1_mailing_address_state"]) {
      formModel["SC-Entity-Section_1_mailing_address_state"] =
        responseModel["SC-Entity-Section_1_mailing_address_state"];
    }
    if (responseModel["SC-Entity-Section_1_mailing_address_country"]) {
      formModel["SC-Entity-Section_1_mailing_address_country"] =
        responseModel["SC-Entity-Section_1_mailing_address_country"];
    }
    if (responseModel["SC-Entity-Section_1_mailing_address_postal"]) {
      formModel["SC-Entity-Section_1_mailing_address_postal"] =
        responseModel["SC-Entity-Section_1_mailing_address_postal"];
    }

    // Map specific checkbox field for Section 2 unable to procure TIN
    if (responseModel["SC-Entity-Section_2_unable_to_procure_a_tin"]) {
      formModel["SC-Entity-Section_2_unable_to_procure_a_tin"] = true;
    }

    // Map additional response fields to form fields
    Object.keys(responseModel).forEach((responseField) => {
      const value = responseModel[responseField];
      if (value !== undefined && value !== null && value !== "") {
        // Direct assignment for fields that exist in form model
        if ((formModel as any)[responseField] !== undefined && responseField !== "isSigned") {
          (formModel as any)[responseField] = value;
        }
      }
    });

    // Map PART III - CRS Classification fields with hyphen to underscore conversion
    const crsFieldMappings: Record<string, string> = {
      "SC-Entity-Section_3_financial_institution": "SC-Entity-Section_3_financial_institution",
      "SC-Entity-Section_3_fatca_purposes": "SC-Entity-Section_3_fatca_purposes",
      "SC-Entity-Section_3_reporting_financial_institution_under_crs":
        "SC-Entity-Section_3_reporting_financial_institution_under_crs",
      "SC-Entity-Section_3_non_financial_institution_under_crs":
        "SC-Entity-Section_3_non_financial_institution_under_crs",
      "SC-Entity-Section_3_goverment_entity": "SC-Entity-Section_3_goverment_entity",
      "SC-Entity-Section_3_international_org": "SC-Entity-Section_3_international_org",
      "SC-Entity-Section_3_central_bank": "SC-Entity-Section_3_central_bank",
      "SC-Entity-Section_3_broad_participation_retirement_fund":
        "SC-Entity-Section_3_broad_participation_retirement_fund",
      "SC-Entity-Section_3_narrow_participation_retirement_fund":
        "SC-Entity-Section_3_narrow_participation_retirement_fund",
      "SC-Entity-Section_3_pension_fund": "SC-Entity-Section_3_pension_fund",
      "SC-Entity-Section_3_exempt_collective_investment":
        "SC-Entity-Section_3_exempt_collective_investment",
      "SC-Entity-Section_3_trust_trustee": "SC-Entity-Section_3_trust_trustee",
      "SC-Entity-Section_3_qualified_credit_card_issuer":
        "SC-Entity-Section_3_qualified_credit_card_issuer",
      "SC-Entity-Section_3_other_entity": "SC-Entity-Section_3_other_entity",
      "SC-Entity-Section_3_non_financial_institution_under_crs_resident":
        "SC-Entity-Section_3_non_financial_institution_under_crs_resident",
      "SC-Entity-Section_3_investment_entity_managed_by_company_listed":
        "SC-Entity-Section_3_investment_entity_managed_by_company_listed",
      "SC-Entity-Section_3_investment_entity": "SC-Entity-Section_3_investment_entity",
      "SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ":
        "SC-Entity-Section_3_investment_entity_managed_by_widely_held_civ",
      "SC-Entity-Section_3_investment_entity_managed_by_pension_fund":
        "SC-Entity-Section_3_investment_entity_managed_by_pension_fund",
      "SC-Entity-Section_3_other_investment_entity": "SC-Entity-Section_3_other_investment_entity",
      "SC-Entity-Section_3_non_financial_institution":
        "SC-Entity-Section_3_non_financial_institution",
      "SC-Entity-Section_3_active_nfe": "SC-Entity-Section_3_active_nfe",
      "SC-Entity-Section_3_regularly_traded_corporation":
        "SC-Entity-Section_3_regularly_traded_corporation",
      "SC-Entity-Section_3_stock_exchange_name": "SC-Entity-Section_3_stock_exchange_name",
      "SC-Entity-Section_3_regularly_traded_corporation_name":
        "SC-Entity-Section_3_regularly_traded_corporation_name",
      "SC-Entity-Section_3_active_nfe_goverment_entity":
        "SC-Entity-Section_3_active_nfe_goverment_entity",
      "SC-Entity-Section_3_active_nfe_international_org":
        "SC-Entity-Section_3_active_nfe_international_org",
      "SC-Entity-Section_3_active_nfe_other": "SC-Entity-Section_3_active_nfe_other",
      "SC-Entity-Section_3_passive_nfe": "SC-Entity-Section_3_passive_nfe",
    };

    // Map all CRS fields from hyphenated API names to underscored form names
    Object.keys(crsFieldMappings).forEach((apiField) => {
      const formField = crsFieldMappings[apiField];
      if (responseModel[apiField] !== undefined) {
        (formModel as any)[formField] = responseModel[apiField];
        // console.log(`✅ CRS field mapped: ${apiField} → ${formField} = ${responseModel[apiField]}`);
      }
    });

    // Handle Section 2 Tax Purposes table
    if (responseModel["tables"] && responseModel["tables"]["section_2_tax_purposes"]) {
      const section2Data = responseModel["tables"]["section_2_tax_purposes"];

      if (Array.isArray(section2Data)) {
        section2Data.forEach((row: any, index: number) => {
          const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)

          if (row.tax_residence !== undefined) {
            formModel[`sc_11 Jurisdiction of Tax Residence${jurisdictionNum}`] = row.tax_residence;
          }

          if (row.tax_id !== undefined) {
            formModel[`sc_11 Tax Identification Number${jurisdictionNum}`] = row.tax_id;
          }

          if (row.tax_reference_number !== undefined) {
            formModel[`sc_11 Tax Reference Number Type${jurisdictionNum}`] =
              row.tax_reference_number;
          }

          if (row.reason_for_non_tin !== undefined) {
            formModel[`sc_11 non-availability of TIN ${jurisdictionNum}`] = row.reason_for_non_tin;
          }
        });
      }
    }

    // Handle Section 4 Tax Purposes table (Controlling Person)
    if (responseModel["tables"] && responseModel["tables"]["section_4_tax_purposes"]) {
      const section4Data = responseModel["tables"]["section_4_tax_purposes"];

      if (Array.isArray(section4Data)) {
        section4Data.forEach((row: any, index: number) => {
          const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)

          if (row.tax_residence !== undefined && row.tax_residence !== null) {
            formModel[`SC-Entity-Controlling_person_jurisdiction_${jurisdictionNum}`] =
              row.tax_residence;
          }

          if (row.tax_reference_number !== undefined) {
            formModel[`SC-Entity-Controlling_person_tax_id_${jurisdictionNum}`] =
              row.tax_reference_number;
          }

          if (row.tax_id !== undefined) {
            formModel[`SC-Entity-Controlling_person_tax_reference_type_${jurisdictionNum}`] =
              row.tax_id;
          }

          if (row.reason_for_non_tin !== undefined) {
            formModel[`SC-Entity-Controlling_person_tin_reason_${jurisdictionNum}`] =
              row.reason_for_non_tin;
          }
        });
      }
    }

    // Handle Section 3 Controlling Persons table
    if (responseModel["tables"] && responseModel["tables"]["section_3_controlling_persons"]) {
      const section3Data = responseModel["tables"]["section_3_controlling_persons"];

      if (Array.isArray(section3Data)) {
        section3Data.forEach((row: any, index: number) => {
          const personNum = index + 1; // Start from 1 (Person 1, 2, 3, etc.)

          if (row.name !== undefined && personNum <= 10) {
            formModel[`SC-Entity-Controlling_person_${personNum}`] = row.name;
          }
        });
      }
    }

    // Fallback: Check for old table data structure for backward compatibility
    let tableData = null;
    if (responseModel["tables"] && responseModel["tables"]["table_0"]) {
      tableData = responseModel["tables"]["table_0"];
    } else if (responseModel["table_0"]) {
      tableData = responseModel["table_0"];
    }

    if (tableData && Array.isArray(tableData)) {
      tableData.forEach((row: any, index: number) => {
        const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)

        // NOTE: The API response has shifted columns!
        // "Jurisdiction of Tax Residence*" contains the row number (2, 3, 4)
        // "Tax Identification Number*" contains the actual jurisdiction (Denmark, Argentina, Chile)
        // "Tax Reference Number Type" contains the actual tax ID (352456-9374, etc.)
        // "Reason for non-availability of TIN (if applicable)" contains the actual reference type (FTIN, etc.)

        // Corrected mapping with shifted columns:
        if (row["Tax Identification Number*"]) {
          formModel[`sc_11 Jurisdiction of Tax Residence${jurisdictionNum}`] =
            row["Tax Identification Number*"];
        }

        if (row["Tax Reference Number Type"]) {
          formModel[`sc_11 Tax Identification Number${jurisdictionNum}`] =
            row["Tax Reference Number Type"];
        }

        if (row["Reason for non-availability of TIN (if applicable)"]) {
          formModel[`sc_11 Tax Reference Number Type${jurisdictionNum}`] =
            row["Reason for non-availability of TIN (if applicable)"];
        }

        // The 5th column (which should be "reason") doesn't exist in this data structure
        // So we skip mapping for non-availability reason
      });
    }

    // Map table_1 for controlling person jurisdictions
    let table1Data = null;
    if (responseModel["tables"] && responseModel["tables"]["table_1"]) {
      table1Data = responseModel["tables"]["table_1"];
    } else if (responseModel["table_1"]) {
      table1Data = responseModel["table_1"];
    }

    if (table1Data && Array.isArray(table1Data)) {
      table1Data.forEach((row: any, index: number) => {
        const jurisdictionNum = index + 2; // Start from 2 (Jurisdiction 2, 3, 4)

        // Same shifted column mapping for controlling person table
        if (row["Tax Identification Number*"]) {
          formModel[`SC-Entity-Controlling_person_jurisdiction_${jurisdictionNum}`] =
            row["Tax Identification Number*"];
        }

        if (row["Reason for non-availability of TIN (if applicable)"]) {
          formModel[`SC-Entity-Controlling_person_tax_id_${jurisdictionNum}`] =
            row["Reason for non-availability of TIN (if applicable)"];
        }

        if (row["Tax Reference Number Type"]) {
          formModel[`SC-Entity-Controlling_person_tax_reference_type_${jurisdictionNum}`] =
            row["Tax Reference Number Type"];
        }
      });
      console.log("✅ SelfCertEntity: Table_1 data mapping completed");
    } else {
      console.log("❌ SelfCertEntity: No valid table data to process");
    }
  }
}
